import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaHeadset,
  FaPhoneAlt,
  FaTrain,
} from "react-icons/fa";
import RailSearchWidget from "../helpers/RailSearchWidget";
import { railBrands } from "../../data/railBrands";
import { findRailStation, getRailResults } from "../../data/railSearch";

export default function RailResultsPage() {
  const [searchParams] = useSearchParams();
  const origin = searchParams.get("origin") || "Departure";
  const originName = searchParams.get("originName") || origin;
  const dest = searchParams.get("dest") || "Arrival";
  const destName = searchParams.get("destName") || dest;
  const departDate = searchParams.get("departDate") || "";
  const returnDate = searchParams.get("returnDate") || "";
  const passengers = searchParams.get("passengers") || "1";
  const brand = searchParams.get("brand") || "any";
  const tripType = searchParams.get("tripType") || "return";
  const directOnly = searchParams.get("directOnly") === "true";
  const railResults = getRailResults({ origin, dest, brand, passengers });
  const originStation = findRailStation(origin);
  const destStation = findRailStation(dest);

  return (
    <div className="bg-[#f8f9fc] font-sans">
      <RailSearchWidget />

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <nav className="mb-6 flex items-center gap-2 text-sm font-bold text-slate-500">
          <Link to="/" className="transition hover:text-[#2871d3]">
            Home
          </Link>
          <FaArrowRight className="h-3 w-3" />
          <Link to="/rail" className="transition hover:text-[#2871d3]">
            Rail Brands
          </Link>
          <FaArrowRight className="h-3 w-3" />
          <span className="text-slate-950">Rail Search</span>
        </nav>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <main className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)] sm:p-8">
            <p className="mb-3 inline-flex rounded-full bg-blue-50 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-[#2871d3]">
              Rail search
            </p>
            <h1 className="text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">
              {originName} to {destName}
            </h1>
            <div className="mt-5 flex flex-wrap gap-3 text-sm font-black text-slate-600">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#f8f9fc] px-4 py-2">
                <FaCalendarAlt className="text-[#2871d3]" />
                {departDate || "Flexible date"}
              </span>
              {tripType === "return" && returnDate && (
                <span className="inline-flex items-center gap-2 rounded-full bg-[#f8f9fc] px-4 py-2">
                  <FaCalendarAlt className="text-[#2871d3]" />
                  Return {returnDate}
                </span>
              )}
              <span className="inline-flex items-center gap-2 rounded-full bg-[#f8f9fc] px-4 py-2">
                <FaTrain className="text-[#2871d3]" />
                {passengers} {passengers === "1" ? "Passenger" : "Passengers"}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-[#f8f9fc] px-4 py-2">
                <FaCheckCircle className="text-emerald-500" />
                {brand === "any" ? "Any rail brand" : brand}
              </span>
              {directOnly && (
                <span className="inline-flex items-center gap-2 rounded-full bg-[#f8f9fc] px-4 py-2">
                  <FaCheckCircle className="text-emerald-500" />
                  Direct trains only
                </span>
              )}
            </div>

            {railResults.length > 0 ? (
              <div className="mt-8 space-y-4">
                {railResults.map((result) => (
                  <article
                    key={result.id}
                    className="rounded-[1.25rem] border border-slate-200 bg-[#f8f9fc] p-5 transition hover:border-blue-100 hover:bg-white hover:shadow-[0_18px_45px_rgba(40,113,211,0.1)]"
                  >
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-[#2871d3]">
                            {result.brand}
                          </span>
                          <span className="text-sm font-black text-slate-500">
                            {result.train}
                          </span>
                        </div>
                        <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                          <div>
                            <div className="text-2xl font-black text-slate-950">
                              {result.departTime}
                            </div>
                            <div className="mt-1 text-sm font-bold text-slate-500">
                              {originStation?.code || origin}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm font-black text-slate-500">
                            <FaClock className="text-[#2871d3]" />
                            {result.duration}
                          </div>
                          <div className="sm:text-right">
                            <div className="text-2xl font-black text-slate-950">
                              {result.arriveTime}
                            </div>
                            <div className="mt-1 text-sm font-bold text-slate-500">
                              {destStation?.code || dest}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="shrink-0 rounded-2xl bg-white p-4 text-left shadow-sm lg:w-44">
                        <div className="text-xs font-black uppercase tracking-[0.15em] text-slate-400">
                          From
                        </div>
                        <div className="mt-1 text-3xl font-black text-slate-950">
                          ${result.fare}
                        </div>
                        <div className="mt-1 text-xs font-bold text-slate-500">
                          {result.seatsLeft} seats at this fare
                        </div>
                        <a
                          href="tel:+18883385981"
                          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2871d3] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#155bc2]"
                        >
                          <FaPhoneAlt className="h-3 w-3" />
                          Book
                        </a>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mt-8 rounded-[1.25rem] border border-amber-200 bg-amber-50 p-6">
                <h2 className="text-xl font-black text-slate-950">
                  No matching rail options found
                </h2>
                <p className="mt-3 text-sm font-medium leading-6 text-slate-600">
                  Try choosing stations served by the same rail brand, or switch
                  the rail brand filter to Any Brand.
                </p>
              </div>
            )}
          </main>

          <aside className="space-y-5">
            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <FaHeadset className="text-[#2871d3]" />
                <h2 className="text-lg font-black text-slate-950">
                  Booking support
                </h2>
              </div>
              <p className="mt-3 text-sm font-medium leading-6 text-slate-500">
                Advisors can help compare schedules, fares, and ticket rules.
              </p>
            </div>

            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-lg font-black text-slate-950">
                Rail brands
              </h2>
              <div className="space-y-2">
                {railBrands.map((brand) => (
                  <Link
                    key={brand.slug}
                    to={`/rail/${brand.slug}`}
                    className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-[#f8f9fc] p-3 transition hover:border-blue-100 hover:bg-blue-50"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-sm font-black text-[#2871d3]">
                      {brand.code}
                    </span>
                    <span className="text-sm font-black text-slate-700">
                      {brand.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
